import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useState } from 'react'

import { setFeedbackData, setTableData } from '../../functions/dataHelpers'
import {
  User,
  useCompetitionStore,
  useFeedbackStore,
  useStore,
  useTableStore,
} from '../../functions/store'
import FeedbackModal from './FeedbackModal'

// Home icon for navigation
const HomeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 sm:h-6 sm:w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
)

// Icons for navigation
const TableIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
)

const FeedbackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 sm:h-6 sm:w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
)

const CardsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 sm:h-6 sm:w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
)

const CompetitionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 sm:h-6 sm:w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
    />
  </svg>
)

const ProfileIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 sm:h-6 sm:w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
)

type NavItem = {
  id: string
  label: string
  shortLabel: string
  icon: React.ReactNode
  color: string
  activeColor: string
}

type StepSelectorProps = {
  variant?: 'grid' | 'mobile'
}

export const StepSelector = ({ variant = 'mobile' }: StepSelectorProps) => {
  const user = useStore((state) => state)
  const tableStore = useTableStore((state) => state)
  const feedbackStore = useFeedbackStore((state) => state)
  const _competitionStore = useCompetitionStore((state) => state)

  const { t } = useTranslation('avaliacao360')

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  // Pre-compute translations to avoid rendering issues
  const cardUnavailableTitle = t(
    'cards.cardUnavailableTitle',
    'Card Não Disponível',
  )
  const cardUnavailableHint = t(
    'cards.cardUnavailableHint',
    'Seu card será gerado automaticamente assim que você receber avaliações de seus parceiros de treino.',
  )

  const navItems: NavItem[] = [
    {
      id: 'home',
      label: 'Início',
      shortLabel: 'Início',
      icon: <HomeIcon />,
      color: 'bg-slate-700',
      activeColor: 'border-slate-500',
    },
    {
      id: 'table',
      label: t('table.labels.teamData', 'Dados da Equipe'),
      shortLabel: 'Equipe',
      icon: <TableIcon />,
      color: 'bg-amber-700',
      activeColor: 'border-amber-500',
    },
    {
      id: 'feedback',
      label: t('evaluation.evaluation', 'Avaliação'),
      shortLabel: 'Avaliar',
      icon: <FeedbackIcon />,
      color: 'bg-sky-700',
      activeColor: 'border-sky-500',
    },
    {
      id: 'cards',
      label: 'Cards',
      shortLabel: 'Cards',
      icon: <CardsIcon />,
      color: 'bg-emerald-700',
      activeColor: 'border-emerald-500',
    },
    {
      id: 'competition',
      label: 'Competições',
      shortLabel: 'Comp.',
      icon: <CompetitionIcon />,
      color: 'bg-red-700',
      activeColor: 'border-red-500',
    },
    {
      id: 'profile',
      label: 'Meu Perfil',
      shortLabel: 'Perfil',
      icon: <ProfileIcon />,
      color: 'bg-violet-700',
      activeColor: 'border-violet-500',
    },
  ]

  const handleStepChangeTable = useCallback(
    async (user: User) => {
      user.setUser({ ...user, step: 'table' })
    },
    [user]
  )

  const handleStepChangeFeedback = useCallback(
    async (user: User) => {
      user.setUser({ ...user, step: 'feedback' })
    },
    [user],
  )

  const handleStepChangeCard = useCallback(
    async (user: User) => {
      user.setUser({ ...user, step: 'cards' })
    },
    [user],
  )

  const handleStepChangeCompetition = useCallback(
    async (user: User) => {
      user.setUser({ ...user, step: 'competition' })
    },
    [user],
  )

  const handleStepChangeProfile = useCallback(
    async (user: User) => {
      user.setUser({ ...user, step: 'profile' })
    },
    [user],
  )

  const handleStepChangeHome = useCallback(
    async (user: User) => {
      user.setUser({ ...user, step: 'home' })
    },
    [user],
  )

  useEffect(() => {
    if (user?.step === 'cards') handleStepChangeCard(user)
  }, [user?.group?._id])

  const handleNavClick = async (stepId: string) => {
    switch (stepId) {
      case 'home':
        await handleStepChangeHome(user)
        break
      case 'table':
        await handleStepChangeTable(user)
        break
      case 'feedback':
        await handleStepChangeFeedback(user)
        break
      case 'cards':
        await handleStepChangeCard(user)
        break
      case 'competition':
        await handleStepChangeCompetition(user)
        break
      case 'profile':
        await handleStepChangeProfile(user)
        break
    }
  }

  const currentStep = user.step

  if (variant === 'grid') {
    return (
      <>
        {/* Desktop Grid Navigation */}
        <nav
          className="hidden md:grid grid-cols-3 grid-rows-2 gap-2 w-full"
          role="tablist"
        >
          {navItems.map((item) => {
            const isActive = currentStep === item.id
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                role="tab"
                aria-selected={isActive}
                className={`
                  relative px-3 py-2 rounded-xl font-semibold text-xs lg:text-sm
                  transition-all duration-200 ease-out flex items-center justify-center gap-2
                  overflow-hidden min-w-0
                  ${
                    isActive
                      ? `${item.color} text-white shadow-lg`
                      : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/80 hover:text-white'
                  }
                `}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                <span className="truncate">{item.label}</span>
              </button>
            )
          })}
        </nav>

        {/* Reuse Modal */}
        <FeedbackModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={cardUnavailableTitle}
          message={modalMessage}
          variant="info"
          actionLabel={t('common.understood') || 'Entendi'}
        >
          <div className="bg-slate-800/50 rounded-xl p-4 border border-white/5">
            <p className="text-slate-400 text-sm text-center">
              {cardUnavailableHint}
            </p>
          </div>
        </FeedbackModal>
      </>
    )
  }

  return (
    <>
      {/* Mobile Bottom Navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-lg border-t border-white/10 safe-area-pb"
        role="tablist"
      >
        <div className="flex justify-around items-center h-16 px-2">
          {navItems.map((item) => {
            const isActive = currentStep === item.id
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                role="tab"
                aria-selected={isActive}
                className={`
                  flex flex-col items-center justify-center gap-1
                  w-full h-full py-2 px-1
                  transition-all duration-200 ease-out
                  ${
                    isActive
                      ? 'text-white'
                      : 'text-slate-400 hover:text-slate-200'
                  }
                `}
              >
                <div
                  className={`
                    p-1.5 rounded-xl transition-all duration-200
                    ${isActive ? `${item.color} shadow-lg` : ''}
                  `}
                >
                  {item.icon}
                </div>
                <span
                  className={`text-[10px] font-medium ${
                    isActive ? 'text-white' : 'text-slate-400'
                  }`}
                >
                  {item.shortLabel}
                </span>
                {isActive && (
                  <span
                    className={`absolute top-0 left-1/2 -translate-x-1/2 w-12 h-0.5 rounded-full ${item.color}`}
                  />
                )}
              </button>
            )
          })}
        </div>
      </nav>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={cardUnavailableTitle}
        message={modalMessage}
        variant="info"
        actionLabel={t('common.understood') || 'Entendi'}
      >
        <div className="bg-slate-800/50 rounded-xl p-4 border border-white/5">
          <p className="text-slate-400 text-sm text-center">
            {cardUnavailableHint}
          </p>
        </div>
      </FeedbackModal>
    </>
  )
}
